import React, { useMemo } from 'react';
import Popup from '../other/Popup';

const clampColorChannel = (value) => Math.max(0, Math.min(255, parseInt(value, 10) || 0));
const toHex = (value) => clampColorChannel(value).toString(16).padStart(2, '0');
const rgbToHex = (r, g, b) => `#${toHex(r)}${toHex(g)}${toHex(b)}`;

const normalizeColorToHex = (color) => {
    if (!color) return '#ffffff';

    const normalized = color.toString().trim().toLowerCase();
    if (/^#[0-9a-f]{6}$/i.test(normalized)) return normalized;
    if (/^#[0-9a-f]{3}$/i.test(normalized)) {
        return `#${normalized[1]}${normalized[1]}${normalized[2]}${normalized[2]}${normalized[3]}${normalized[3]}`;
    }

    const rgbMatch = normalized.match(/^rgb\(\s*([0-9]+)\s*,\s*([0-9]+)\s*,\s*([0-9]+)\s*\)$/i);
    if (rgbMatch) {
        return rgbToHex(rgbMatch[1], rgbMatch[2], rgbMatch[3]);
    }

    const rgbaMatch = normalized.match(/^rgba\(\s*([0-9]+)\s*,\s*([0-9]+)\s*,\s*([0-9]+)\s*,\s*[0-9.]+\s*\)$/i);
    if (rgbaMatch) {
        return rgbToHex(rgbaMatch[1], rgbaMatch[2], rgbaMatch[3]);
    }

    if (normalized === 'white') return '#ffffff';
    if (normalized === 'black') return '#000000';
    return '#ffffff';
};

const isLightColor = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 180;
};

function PaintColorPopup({
    colorPopupClosed,
    setColorPopupClosed,
    json,
    paintColor,
    setPaintColor
}) {
    const selectColor = (color) => {
        setPaintColor(normalizeColorToHex(color));
    };

    const existingColors = useMemo(() => {
        if (!json) return [];
        const colorSet = new Set();
        Object.values(json).forEach(item => {
            if (item?.type === 'caixa') {
                const fill = normalizeColorToHex(item?.fill || item?.box?.fill || null);
                if (fill && fill !== '#ffffff') {
                    colorSet.add(fill);
                }
            }
        });
        return [...colorSet];
    }, [json]);

    const normalizedCurrent = normalizeColorToHex(paintColor || '#ffffff');
    const isCustomColor = !existingColors.includes(normalizedCurrent);

    return (
        <Popup
            closed={colorPopupClosed}
            setClosed={setColorPopupClosed}
            width="380px"
            maxWidth="380px"
        >
            <h3 style={{ margin: 0, marginBottom: 16, fontSize: 17 }}>Tria un color</h3>

            {/* Existing colors */}
            {existingColors.length > 0 ? (
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Colors existents</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {existingColors.map(color => {
                            const isSelected = normalizedCurrent === color;
                            return (
                                <button
                                    key={color}
                                    type="button"
                                    onClick={() => selectColor(color)}
                                    title={color}
                                    style={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: 8,
                                        border: isSelected ? '2px solid #2563eb' : '2px solid #d1d5db',
                                        backgroundColor: color,
                                        cursor: 'pointer',
                                        padding: 0,
                                        outline: isSelected ? '2px solid #bfdbfe' : 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'border-color 0.15s, outline 0.15s',
                                    }}
                                >
                                    {isSelected ? (
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke={isLightColor(color) ? '#2563eb' : '#ffffff'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : null}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ) : null}

            {/* Custom color picker */}
            <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Color personalitzat</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <label style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        border: isCustomColor ? '2px solid #2563eb' : '2px solid #d1d5db',
                        backgroundColor: normalizedCurrent,
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden',
                        flexShrink: 0,
                        outline: isCustomColor ? '2px solid #bfdbfe' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'border-color 0.15s, outline 0.15s',
                    }}>
                        <input
                            type="color"
                            value={normalizedCurrent}
                            onChange={(e) => selectColor(e.target.value)}
                            style={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                opacity: 0,
                                cursor: 'pointer',
                            }}
                        />
                        {isCustomColor ? (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ position: 'relative', zIndex: 1, pointerEvents: 'none' }}>
                                <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke={isLightColor(normalizedCurrent) ? '#2563eb' : '#ffffff'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        ) : null}
                    </label>
                    <code style={{
                        fontSize: 13,
                        color: '#374151',
                        backgroundColor: '#f3f4f6',
                        padding: '4px 8px',
                        borderRadius: 6,
                    }}>{normalizedCurrent}</code>
                </div>
            </div>
        </Popup>
    );
}

export default PaintColorPopup;
